/**
 * Scan timing estimates — rolling rate + prior-run baselines per folder.
 * Phase 1 (NAS walk) has no total upfront; we learn from the last completed scan.
 */

export interface ScanTimingBaseline {
  /** Filesystem entries counted during NAS walk (not audio file count). */
  walkEntries: number;
  /** Audio files tagged in phase 2. */
  tagFiles: number;
  /** Milliseconds spent in phase 1. */
  walkMs: number;
  /** Milliseconds spent in phase 2. */
  tagMs: number;
  updatedAt: number;
}

const STORAGE_KEY = 'maestro_scan_timing_baselines';
const MAX_BASELINES = 24;

/** Never render a zero or sub-1 rate — show phase label instead (#422). */
export const MIN_DISPLAY_RATE = 1;

export function finalizeStageDisplayLabel(stage: string | null | undefined): string | null {
  switch (stage) {
    case 'reconciling_totals':
      return 'Reconciling scanned files…';
    case 'checking_attention_items':
      return 'Checking for files that need attention…';
    case 'counting_albums':
      return 'Counting albums…';
    default:
      return null;
  }
}

function displayableRate(rate: number | null | undefined): number | null {
  if (typeof rate !== 'number' || !Number.isFinite(rate) || rate < MIN_DISPLAY_RATE) return null;
  return rate;
}

function normPathKey(path: string): string {
  return path.replace(/\\/g, '/').replace(/\/+$/, '').toLowerCase();
}

function readAllBaselines(): Record<string, ScanTimingBaseline> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAllBaselines(map: Record<string, ScanTimingBaseline>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* quota or private mode */
  }
}

export function getScanBaseline(path: string | null | undefined): ScanTimingBaseline | null {
  if (!path) return null;
  const key = normPathKey(path);
  return readAllBaselines()[key] ?? null;
}

export function saveScanBaseline(path: string, data: Omit<ScanTimingBaseline, 'updatedAt'>): void {
  if (!path) return;
  const key = normPathKey(path);
  const all = readAllBaselines();
  all[key] = { ...data, updatedAt: Date.now() };
  const keys = Object.keys(all).sort(
    (a, b) => (all[b]?.updatedAt ?? 0) - (all[a]?.updatedAt ?? 0),
  );
  if (keys.length > MAX_BASELINES) {
    keys.slice(MAX_BASELINES).forEach((k) => delete all[k]);
  }
  writeAllBaselines(all);
}

/** @param totalSec seconds */
export function formatDuration(totalSec: number | null | undefined): string {
  if (totalSec == null || !Number.isFinite(totalSec)) return '';
  const s = Math.max(0, Math.round(totalSec));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
  }
  return r > 0 ? `${m}m ${r}s` : `${m}m`;
}

export const DISCOVERY_SCAN_PHASES = new Set(['enumerating', 'discovering', 'preparing']);

export function isDiscoveryScanPhase(phase: string | null | undefined): boolean {
  return DISCOVERY_SCAN_PHASES.has(String(phase ?? ''));
}

/** File count while NAS walk is still discovering — total is not final yet. */
export function formatDiscoveredFilesCount(count: number): string {
  return `${count.toLocaleString()} found`;
}

/** Tag-read progress — prefix ~ on total while discovery may still be climbing. */
export function formatTagReadProgress(
  current: number,
  total: number | null | undefined,
  discoveryOngoing = false,
): string {
  if (total != null && total > current && !discoveryOngoing) {
    return `${current.toLocaleString()} of ${total.toLocaleString()} files`;
  }
  const approx = total ?? current;
  return `${current.toLocaleString()} of ~${approx.toLocaleString()} files`;
}

export type ScanTimerParts = {
  stepLabel: string | null;
  elapsedLabel: string;
  detailLabel: string | null;
  remainingSec: number | null;
};

/**
 * Build human-readable timer line for the progress strip.
 */
export function buildScanTimerParts(opts: {
  phase: string;
  elapsedSec: number;
  currentStep?: number | null;
  totalSteps?: number | null;
  etaMs?: number | null;
  filesPerSecond?: number | null;
  baselineKey?: string | null;
  /** Client-computed items/sec during NAS walk when engine does not send rate. */
  walkRate?: number | null;
}): ScanTimerParts {
  const {
    phase,
    elapsedSec,
    currentStep,
    totalSteps,
    etaMs,
    filesPerSecond,
    baselineKey,
    walkRate,
  } = opts;

  const elapsedLabel = formatDuration(elapsedSec) || '0s';

  if (phase === 'enumerating' || phase === 'discovering') {
    const rate = effectiveWalkRate({
      filesPerSecond,
      walkRate,
      currentStep,
      phaseElapsedSec: elapsedSec,
    });
    const count = typeof currentStep === 'number' ? currentStep : 0;

    const remainingSec = estimateWalkRemainingSec({
      currentStep,
      filesPerSecond,
      walkRate,
      baselineKey,
      phaseElapsedSec: elapsedSec,
    });

    let detailLabel: string | null = null;
    if (rate != null && rate >= 1) {
      detailLabel = `~${Math.round(rate).toLocaleString()} items/sec on NAS`;
    }
    if (remainingSec != null) {
      const rem = formatDuration(remainingSec);
      detailLabel = detailLabel
        ? `${detailLabel} · ~${rem} left`
        : `~${rem} left`;
    } else if (!detailLabel) {
      detailLabel = 'calculating…';
    }

    return {
      stepLabel: 'Step 1 of 2 — NAS walk',
      elapsedLabel,
      detailLabel,
      remainingSec,
    };
  }

  if (phase === 'scanning') {
    const etaSec = typeof etaMs === 'number' && etaMs >= 0
      ? Math.round(etaMs / 1000)
      : null;
    const rate = displayableRate(filesPerSecond);
    let detailLabel: string | null = null;

    if (etaSec != null) {
      detailLabel = `~${formatDuration(etaSec)} remaining`;
    } else if (
      typeof currentStep === 'number'
      && typeof totalSteps === 'number'
      && totalSteps > 0
      && elapsedSec > 5
    ) {
      const frac = currentStep / totalSteps;
      if (frac > 0.02 && frac < 1) {
        const est = Math.max(0, Math.round((elapsedSec / frac) - elapsedSec));
        detailLabel = `~${formatDuration(est)} remaining`;
      }
    }

    if (rate != null) {
      detailLabel = detailLabel
        ? `${detailLabel} · ~${Math.round(rate)} files/sec`
        : `~${Math.round(rate)} files/sec`;
    } else if (!detailLabel) {
      detailLabel = 'Reading file tags…';
    }

    return {
      stepLabel: 'Step 2 of 2 — reading tags',
      elapsedLabel,
      detailLabel: detailLabel ?? 'estimating…',
      remainingSec: etaSec,
    };
  }

  if (phase === 'finalizing' || phase === 'indexing') {
    return {
      stepLabel: 'Step 3 — updating library',
      elapsedLabel,
      detailLabel: null,
      remainingSec: null,
    };
  }

  return {
    stepLabel: null,
    elapsedLabel,
    detailLabel: null,
    remainingSec: null,
  };
}

export function formatScanTimerLine(parts: ScanTimerParts): string {
  const chunks = [
    parts.stepLabel,
    `${parts.elapsedLabel} elapsed`,
    parts.detailLabel,
  ].filter(Boolean);
  return chunks.join(' · ');
}

/** Clock display for elapsed / countdown (m:ss). */
export function formatClock(totalSec: number | null | undefined): string {
  if (totalSec == null || !Number.isFinite(totalSec)) return '—';
  const s = Math.max(0, Math.round(totalSec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

export type PipelineStepStatus = 'pending' | 'active' | 'done';

export type ScanPipelineStep = {
  id: 'walk' | 'tags' | 'finalize';
  title: string;
  status: PipelineStepStatus;
  /** Static estimate shown while pending — only when connector supplies a credible ETA. */
  pendingEstimate: string | null;
  /** Estimated seconds for pending step — backend-justified only. */
  pendingEstimateSec: number | null;
  /** True when remainingSec is derived from connector counts/rates, not client guesses. */
  credibleRemaining: boolean;
};

export type ScanPipeline = {
  steps: ScanPipelineStep[];
  activeStepId: ScanPipelineStep['id'] | null;
  /** Detail line for the active step (rate, file counts, etc.). */
  activeDetail: string | null;
  /** Raw remaining seconds before live countdown smoothing — null unless backend-justified. */
  activeRemainingSec: number | null;
  /** True when activeRemainingSec is connector-backed (safe to show a countdown). */
  activeRemainingCredible: boolean;
};

/** Connector finalize heartbeats emit eta_ms: 0 — never invent a countdown (#501). */
const CONNECTOR_HANDOFF_FINALIZE = {
  phase: 'finalize',
  fields: [
    'albums_written',
    'albums_total',
    'finalize_eta_ms',
    'indexed_count (during finalize)',
  ],
} as const;

export { CONNECTOR_HANDOFF_FINALIZE };

/** Remaining seconds from connector eta_ms or file counts + files_per_second only. */
export function credibleTagRemainingSec(opts: {
  etaMs?: number | null;
  globalWalkFiles?: number | null;
  globalTerminalFiles?: number | null;
  filesPerSecond?: number | null;
  smoothedTagFps?: number | null;
  globalEtaEstimating?: boolean;
  globalEtaSec?: number | null;
  currentStep?: number | null;
  totalSteps?: number | null;
}): number | null {
  if (opts.globalEtaEstimating) return null;
  if (typeof opts.etaMs === 'number' && opts.etaMs > 0) {
    return Math.round(opts.etaMs / 1000);
  }

  const walk = opts.globalWalkFiles ?? opts.totalSteps;
  const terminal = opts.globalTerminalFiles ?? opts.currentStep;
  if (walk != null && walk > 0 && terminal != null && terminal >= 0) {
    const remaining = Math.max(0, walk - terminal);
    const fps = displayableRate(
      (opts.smoothedTagFps && opts.smoothedTagFps > 0)
        ? opts.smoothedTagFps
        : opts.filesPerSecond,
    );
    if (remaining > 0 && fps != null) {
      return Math.round(remaining / fps);
    }
  }

  if (opts.globalEtaSec != null && opts.globalEtaSec > 0) {
    return opts.globalEtaSec;
  }

  return null;
}

/**
 * Percent for the banner — only when tag-read has a real walk/terminal denominator.
 * Walk and finalize never show a percent (#501).
 */
export function credibleScanPercent(opts: {
  phase: string;
  progress?: number | null;
  globalWalkFiles?: number | null;
  globalTerminalFiles?: number | null;
  globalEtaEstimating?: boolean;
  scanWalkStillRunning?: boolean;
}): number | null {
  const phase = String(opts.phase ?? '');
  const isFinalize = phase === 'finalizing' || phase === 'indexing' || phase === 'walk_complete';
  const isWalk = isDiscoveryScanPhase(phase) || phase === 'reconciling' || phase === 'preparing';
  if (isFinalize || isWalk) return null;
  if (opts.globalEtaEstimating || opts.scanWalkStillRunning) return null;

  const walk = opts.globalWalkFiles;
  const terminal = opts.globalTerminalFiles;
  if (walk != null && walk > 0 && terminal != null && terminal >= 0) {
    const pct = Math.round((terminal / walk) * 100);
    return Math.min(100, Math.max(0, pct));
  }

  if (phase === 'scanning' && typeof opts.progress === 'number' && opts.progress > 0 && opts.progress < 1) {
    return Math.min(100, Math.max(0, Math.round(opts.progress * 100)));
  }

  return null;
}

function getAllBaselines(): ScanTimingBaseline[] {
  return Object.values(readAllBaselines());
}

function globalMsPerWalkEntry(): number | null {
  const all = getAllBaselines();
  if (!all.length) return null;
  const sum = all.reduce((acc, b) => acc + b.walkMs / Math.max(b.walkEntries, 1), 0);
  return sum / all.length;
}

function globalTagWalkRatio(): number {
  const all = getAllBaselines();
  if (!all.length) return 1.8;
  const ratios = all
    .filter((b) => b.walkMs > 0)
    .map((b) => b.tagMs / b.walkMs);
  if (!ratios.length) return 1.8;
  return ratios.reduce((a, b) => a + b, 0) / ratios.length;
}

function findSimilarWalkBaseline(entries: number): ScanTimingBaseline | null {
  const all = getAllBaselines();
  if (!all.length) return null;
  const sorted = [...all].sort((a, b) => {
    const da = Math.abs(a.walkEntries - entries);
    const db = Math.abs(b.walkEntries - entries);
    return da - db;
  });
  return sorted[0] ?? null;
}

function effectiveWalkRate(opts: {
  filesPerSecond?: number | null;
  walkRate?: number | null;
  currentStep?: number | null;
  walkFileCount?: number | null;
  phaseElapsedSec?: number;
}): number | null {
  if (opts.filesPerSecond && opts.filesPerSecond > 0) return opts.filesPerSecond;
  if (opts.walkRate && opts.walkRate > 0) return opts.walkRate;
  const count = typeof opts.walkFileCount === 'number' && opts.walkFileCount > 0
    ? opts.walkFileCount
    : (typeof opts.currentStep === 'number' ? opts.currentStep : 0);
  const elapsed = opts.phaseElapsedSec ?? 0;
  if (count > 200 && elapsed >= 15) return count / elapsed;
  return null;
}

function applyWalkAntiStallFloor(
  remaining: number,
  count: number,
  rate: number,
  elapsed: number,
): number {
  if (rate < 15 || elapsed < 30) return Math.max(0, remaining);
  const minFromRate = Math.round((count * 0.06) / rate);
  const minFromElapsed = Math.round(elapsed * 0.2);
  const floor = Math.min(Math.max(minFromRate, minFromElapsed), 900);
  return Math.max(remaining, floor);
}

function estimateWalkRemainingSec(opts: {
  currentStep?: number | null;
  walkFileCount?: number | null;
  filesPerSecond?: number | null;
  walkRate?: number | null;
  baselineKey?: string | null;
  phaseElapsedSec?: number;
}): number | null {
  const count = typeof opts.walkFileCount === 'number' && opts.walkFileCount > 0
    ? opts.walkFileCount
    : (typeof opts.currentStep === 'number' ? opts.currentStep : 0);
  const elapsed = opts.phaseElapsedSec ?? 0;
  const rate = effectiveWalkRate(opts);
  if (rate == null || rate < 1 || count < 100) return null;

  const pathBaseline = getScanBaseline(opts.baselineKey ?? null);
  const similarBaseline = findSimilarWalkBaseline(count);
  const baseline = pathBaseline ?? similarBaseline;

  let remaining: number | null = null;

  if (baseline && count > 200) {
    const baselineTarget = baseline.walkEntries;

    if (count <= baselineTarget * 1.02) {
      remaining = Math.round((baselineTarget - count) / rate);
      if (remaining <= 0) {
        remaining = Math.round((count * 0.05) / rate);
      }
    }
  }

  if (remaining == null || (pathBaseline && count > pathBaseline.walkEntries * 1.02)) {
    const extraFraction = Math.max(0.05, Math.min(0.2, 0.18 - elapsed / 7200));
    remaining = Math.round((count * extraFraction) / rate);
  }

  if (remaining == null && baseline && count > 500) {
    const msPerEntry = globalMsPerWalkEntry();
    if (msPerEntry != null) {
      const projectedTotal = Math.max(count * 1.12, count + rate * 45);
      const estTotalSec = (projectedTotal * msPerEntry) / 1000;
      remaining = Math.round(estTotalSec - elapsed);
    }
  }

  if (remaining == null && elapsed >= 20 && count > 500) {
    const progressFraction = Math.min(0.75, elapsed / (elapsed + 480));
    const estimatedTotalEntries = count / Math.max(progressFraction, 0.12);
    remaining = Math.round((estimatedTotalEntries - count) / rate);
  }

  if (remaining == null) return null;

  if (remaining <= 0 && rate > 10) {
    remaining = Math.round((count * 0.04) / rate);
  }

  remaining = applyWalkAntiStallFloor(Math.max(0, remaining), count, rate, elapsed);
  return remaining;
}

function estimateTagPendingSec(opts: {
  baselineKey?: string | null;
  walkRemainingSec: number | null;
  currentStep?: number | null;
  walkFileCount?: number | null;
  phaseElapsedSec?: number;
}): { sec: number | null; label: string } {
  const baseline = getScanBaseline(opts.baselineKey ?? null);
  if (baseline?.tagMs) {
    const sec = Math.max(30, Math.round(baseline.tagMs / 1000));
    return { sec, label: `~${formatDuration(sec)} est.` };
  }

  const count = typeof opts.walkFileCount === 'number' && opts.walkFileCount > 0
    ? opts.walkFileCount
    : (typeof opts.currentStep === 'number' ? opts.currentStep : 0);
  const similar = count > 0 ? findSimilarWalkBaseline(count) : null;
  if (similar?.tagMs) {
    const sec = Math.max(30, Math.round(similar.tagMs / 1000));
    return { sec, label: `~${formatDuration(sec)} est.` };
  }

  if (opts.walkRemainingSec != null) {
    const sec = Math.max(60, Math.round(opts.walkRemainingSec * globalTagWalkRatio()));
    return { sec, label: `~${formatDuration(sec)} est.` };
  }

  const elapsed = opts.phaseElapsedSec ?? 0;
  if (elapsed >= 30) {
    const sec = Math.max(60, Math.round(elapsed * globalTagWalkRatio()));
    return { sec, label: `~${formatDuration(sec)} est.` };
  }

  return { sec: null, label: 'calculating…' };
}

function estimateTagRemainingSec(opts: {
  elapsedSec: number;
  currentStep?: number | null;
  totalSteps?: number | null;
  etaMs?: number | null;
}): number | null {
  if (typeof opts.etaMs === 'number' && opts.etaMs >= 0) {
    return Math.round(opts.etaMs / 1000);
  }
  if (
    typeof opts.currentStep === 'number'
    && typeof opts.totalSteps === 'number'
    && opts.totalSteps > 0
    && opts.elapsedSec > 5
  ) {
    const frac = opts.currentStep / opts.totalSteps;
    if (frac > 0.02 && frac < 1) {
      return Math.max(0, Math.round((opts.elapsedSec / frac) - opts.elapsedSec));
    }
  }
  return null;
}

/**
 * Three-step scan pipeline: NAS walk → tag read → library refresh.
 */
export function buildScanPipeline(opts: {
  phase: string;
  elapsedSec: number;
  phaseElapsedSec?: number;
  currentStep?: number | null;
  totalSteps?: number | null;
  walkFileCount?: number | null;
  etaMs?: number | null;
  filesPerSecond?: number | null;
  baselineKey?: string | null;
  walkRate?: number | null;
  /** v153 per-source interleave — use aggregate phase + global totals for ETA. */
  interleavedMultiSource?: boolean;
  aggregatePhase?: 'walking' | 'reading_tags' | 'finishing' | 'idle';
  sourcesCompleted?: number | null;
  sourcesTotal?: number | null;
  globalWalkFiles?: number | null;
  globalTerminalFiles?: number | null;
  smoothedTagFps?: number | null;
  globalEtaSec?: number | null;
  globalDirectTotals?: boolean;
  /** Walk still discovering files — global total is provisional. */
  scanWalkStillRunning?: boolean;
  /** Suppress stale low ETA while walk total may still grow. */
  globalEtaEstimating?: boolean;
  /** Connector finalize_stage during phase=finalize (#422). */
  finalizeStage?: string | null;
}): ScanPipeline {
  const phaseElapsedSec = opts.phaseElapsedSec ?? 0;

  const phase = opts.phase;
  const interleaved = opts.interleavedMultiSource === true;
  const aggPhase = opts.aggregatePhase;
  const tagInterleaveStarted = interleaved && (
    opts.globalDirectTotals === true
    || (opts.sourcesCompleted ?? 0) > 0
    || aggPhase === 'reading_tags'
    || aggPhase === 'finishing'
  );

  let isWalk = phase === 'enumerating' || phase === 'discovering' || phase === 'reconciling' || phase === 'preparing';
  let isTags = phase === 'scanning';
  const isFinalize = phase === 'finalizing' || phase === 'indexing' || phase === 'walk_complete';

  if (interleaved && aggPhase) {
    if (aggPhase === 'walking') {
      isWalk = true;
      isTags = false;
    } else if (aggPhase === 'reading_tags') {
      isWalk = false;
      isTags = true;
    } else if (aggPhase === 'finishing') {
      isWalk = false;
      isTags = false;
    }
  }

  let tagRemaining: number | null = null;
  if (isTags) {
    tagRemaining = credibleTagRemainingSec({
      etaMs: opts.etaMs,
      globalWalkFiles: opts.globalWalkFiles,
      globalTerminalFiles: opts.globalTerminalFiles,
      filesPerSecond: opts.filesPerSecond,
      smoothedTagFps: opts.smoothedTagFps,
      globalEtaEstimating: opts.globalEtaEstimating,
      globalEtaSec: opts.globalEtaSec,
      currentStep: opts.currentStep,
      totalSteps: opts.totalSteps,
    });
  }

  let activeDetail: string | null = null;
  if (isWalk) {
    const rate = displayableRate(opts.filesPerSecond);
    const count = typeof opts.walkFileCount === 'number' && opts.walkFileCount > 0
      ? opts.walkFileCount
      : (typeof opts.currentStep === 'number' ? opts.currentStep : 0);
    if (count > 0) activeDetail = formatDiscoveredFilesCount(count);
    if (rate != null) {
      activeDetail = activeDetail
        ? `${activeDetail} · ~${Math.round(rate).toLocaleString()}/sec`
        : `~${Math.round(rate).toLocaleString()} items/sec on NAS`;
    } else if (!activeDetail) {
      activeDetail = 'Finding files on NAS…';
    }
  } else if (isTags) {
    if ((interleaved || opts.globalDirectTotals)
      && opts.globalWalkFiles != null && opts.globalTerminalFiles != null) {
      activeDetail = formatTagReadProgress(
        opts.globalTerminalFiles,
        opts.globalWalkFiles,
        false,
      );
    } else {
      const cur = typeof opts.currentStep === 'number' ? opts.currentStep : null;
      const tot = typeof opts.totalSteps === 'number' ? opts.totalSteps : null;
      if (cur != null && tot != null && tot > 0) {
        activeDetail = formatTagReadProgress(cur, tot, false);
      } else if (cur != null) {
        activeDetail = `${cur.toLocaleString()} files read`;
      }
    }
    const rate = displayableRate(
      (opts.smoothedTagFps && opts.smoothedTagFps > 0)
        ? opts.smoothedTagFps
        : opts.filesPerSecond,
    );
    if (rate != null && activeDetail) {
      activeDetail = `${activeDetail} · ~${Math.round(rate)}/sec`;
    } else if (!activeDetail) {
      activeDetail = 'Reading file tags…';
    }
  } else if (isFinalize) {
    activeDetail = finalizeStageDisplayLabel(opts.finalizeStage) ?? null;
  }

  const folderN = typeof opts.currentStep === 'number' ? opts.currentStep : null;
  const folderTotal = typeof opts.sourcesTotal === 'number' ? opts.sourcesTotal : null;
  const walkTitle = interleaved && folderN != null && folderTotal != null
    ? `Walking NAS folders (folder ${folderN}/${folderTotal})`
    : 'Walking NAS folders';
  const tagsTitle = interleaved && folderN != null && folderTotal != null
    ? `Reading file tags (folder ${folderN}/${folderTotal})`
    : 'Reading file tags';

  const steps: ScanPipelineStep[] = [
    {
      id: 'walk',
      title: walkTitle,
      status: isWalk ? 'active' : (isTags || isFinalize ? 'done' : 'pending'),
      pendingEstimate: null,
      pendingEstimateSec: null,
      credibleRemaining: false,
    },
    {
      id: 'tags',
      title: tagsTitle,
      status: isTags ? 'active' : isFinalize ? 'done' : 'pending',
      pendingEstimate: null,
      pendingEstimateSec: null,
      credibleRemaining: false,
    },
    {
      id: 'finalize',
      title: finalizeStageDisplayLabel(opts.finalizeStage) ?? 'Updating album library',
      status: isFinalize ? 'active' : 'pending',
      pendingEstimate: null,
      pendingEstimateSec: null,
      credibleRemaining: false,
    },
  ];

  const activeStepId = isWalk ? 'walk' : isTags ? 'tags' : isFinalize ? 'finalize' : null;
  const activeRemainingSec = isTags ? tagRemaining : null;
  const activeRemainingCredible = isTags && tagRemaining != null;

  return {
    steps,
    activeStepId,
    activeDetail,
    activeRemainingSec,
    activeRemainingCredible,
  };
}

/** Sum of active countdown + pending step estimates — only when every remaining step is backend-justified (#501). */
export function estimateTotalRemainingSec(
  pipeline: ScanPipeline,
  liveActiveRemainingSec: number | null,
  _globalEtaSec?: number | null,
  globalEtaEstimating?: boolean,
): number | null {
  if (globalEtaEstimating) return null;
  if (!pipeline.activeStepId) return null;
  if (!pipeline.activeRemainingCredible) return null;

  const activeRemaining = liveActiveRemainingSec ?? pipeline.activeRemainingSec;
  if (activeRemaining == null || activeRemaining <= 0) return null;

  const pendingSteps = pipeline.steps.filter((s) => s.status === 'pending');
  if (pendingSteps.some((s) => s.pendingEstimateSec == null || !s.credibleRemaining)) {
    return null;
  }

  let total = activeRemaining;
  for (const step of pendingSteps) {
    if (step.pendingEstimateSec != null) total += step.pendingEstimateSec;
  }

  return total > 0 ? total : null;
}
